import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('lemai_token') || ''}` });

type JobStatus = 'new' | 'assigned' | 'in_progress' | 'pending_review' | 'closed';

interface Job {
  id: string;
  type: string;
  site: string;
  priority: 'Critical' | 'High' | 'Medium' | 'Low';
  status: JobStatus;
  assignedTo: string;
  description: string;
  createdAt: string;
  linkedAsset?: string;
}

interface Tour {
  id: string;
  officer: string;
  site: string;
  startTime: string;
  endTime?: string;
  status: 'in-progress' | 'completed' | 'scheduled' | 'overdue' | 'missed';
  completionPct: number;
  checkpoints: { id: string; name: string; location: string; completed: boolean; completedAt?: string }[];
}

const PRIORITY_COLOR: Record<string, string> = {
  Critical: '#ef4444', High: '#f59e0b', Medium: '#3b82f6', Low: '#555555',
};
const STATUS_COLOR: Record<JobStatus, string> = {
  new: '#555555', assigned: '#a855f7', in_progress: '#f59e0b',
  pending_review: '#3b82f6', closed: '#22c55e',
};
const STATUS_LABEL: Record<JobStatus, string> = {
  new: 'New', assigned: 'Assigned', in_progress: 'In Progress',
  pending_review: 'Pending Review', closed: 'Closed',
};

function fmtTime(iso: string) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}
function fmtDate(iso: string) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

// ─── Job Card ────────────────────────────────────────────────────────────────

function JobCard({ job, onUpdateStatus }: { job: Job; onUpdateStatus: (id: string, status: JobStatus) => void }) {
  const [expanded, setExpanded] = useState(false);

  const nextStatus: Partial<Record<JobStatus, JobStatus>> = {
    new: 'in_progress',
    assigned: 'in_progress',
    in_progress: 'pending_review',
  };
  const nextLabel: Partial<Record<JobStatus, string>> = {
    new: 'Start Job',
    assigned: 'Start Job',
    in_progress: 'Mark Complete',
  };

  return (
    <div style={{
      background: '#111111', border: '1px solid #1a1a1a',
      borderRadius: '5px', overflow: 'hidden', marginBottom: '10px',
    }}>
      <div
        onClick={() => setExpanded(e => !e)}
        style={{ padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}
      >
        <div style={{
          width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0,
          background: PRIORITY_COLOR[job.priority],
          boxShadow: job.priority === 'Critical' ? `0 0 8px ${PRIORITY_COLOR[job.priority]}` : 'none',
        }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {job.type}
          </div>
          <div style={{ fontSize: '11px', color: '#555555', marginTop: '2px' }}>
            {job.site} · {fmtDate(job.createdAt)}
          </div>
        </div>
        <div style={{
          fontSize: '10px', fontWeight: 600, padding: '3px 8px', borderRadius: '6px',
          background: `${STATUS_COLOR[job.status]}22`, color: STATUS_COLOR[job.status],
        }}>
          {STATUS_LABEL[job.status]}
        </div>
        <span style={{ color: '#555555', fontSize: '11px', marginLeft: '4px' }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div style={{ padding: '0 16px 14px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ fontSize: '12px', color: '#a0a0a0', marginTop: '10px', lineHeight: '1.5' }}>
            {job.description || 'No description provided.'}
          </div>
          {job.linkedAsset && (
            <div style={{ fontSize: '11px', color: '#555555', marginTop: '8px' }}>
              Linked asset: <span style={{ color: '#93c5fd' }}>{job.linkedAsset}</span>
            </div>
          )}
          <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {nextStatus[job.status] && (
              <button
                onClick={() => onUpdateStatus(job.id, nextStatus[job.status]!)}
                style={{
                  padding: '8px 16px', borderRadius: '4px', border: 'none', cursor: 'pointer',
                  background: '#3b82f6', color: 'white', fontSize: '12px', fontWeight: 600,
                }}
              >
                {nextLabel[job.status]}
              </button>
            )}
            {job.status === 'closed' && (
              <div style={{ fontSize: '12px', color: '#22c55e', display: 'flex', alignItems: 'center', gap: '6px' }}>
                ✓ Completed
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tour Card ───────────────────────────────────────────────────────────────

function TourCard({ tour, onCheckpoint }: { tour: Tour; onCheckpoint: (tourId: string, cpId: string) => void }) {
  const [expanded, setExpanded] = useState(false);
  const statusColor: Record<string, string> = {
    'in-progress': '#f59e0b', completed: '#22c55e',
    scheduled: '#3b82f6', overdue: '#ef4444', missed: '#555555',
  };

  return (
    <div style={{
      background: '#111111', border: '1px solid #1a1a1a',
      borderRadius: '5px', overflow: 'hidden', marginBottom: '10px',
    }}>
      <div onClick={() => setExpanded(e => !e)} style={{ padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span style={{ fontSize: '18px' }}>🚶</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'white' }}>{tour.site}</div>
          <div style={{ fontSize: '11px', color: '#555555', marginTop: '2px' }}>
            Officer: {tour.officer} · Start: {fmtTime(tour.startTime)}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: '10px', fontWeight: 600, color: statusColor[tour.status] || '#555555', textTransform: 'capitalize' }}>
            {tour.status.replace('-', ' ')}
          </div>
          <div style={{ fontSize: '11px', color: '#555555', marginTop: '2px' }}>
            {tour.completionPct}%
          </div>
        </div>
        <span style={{ color: '#555555', fontSize: '11px', marginLeft: '4px' }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {/* Progress bar */}
      <div style={{ height: '3px', background: 'rgba(255,255,255,0.06)', margin: '0 16px' }}>
        <div style={{ height: '100%', width: `${tour.completionPct}%`, background: statusColor[tour.status] || '#3b82f6', borderRadius: '2px', transition: 'width 0.3s ease' }} />
      </div>

      {expanded && (
        <div style={{ padding: '12px 16px 14px', borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: '3px' }}>
          <div style={{ fontSize: '11px', color: '#555555', marginBottom: '10px', fontWeight: 600 }}>
            CHECKPOINTS ({tour.checkpoints.filter(c => c.completed).length}/{tour.checkpoints.length})
          </div>
          {tour.checkpoints.map(cp => (
            <div key={cp.id} style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '8px 10px', borderRadius: '4px', marginBottom: '4px',
              background: cp.completed ? 'rgba(34,197,94,0.06)' : 'rgba(255,255,255,0.02)',
              border: `1px solid ${cp.completed ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.05)'}`,
            }}>
              <div style={{
                width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0,
                background: cp.completed ? '#22c55e' : 'rgba(255,255,255,0.06)',
                border: cp.completed ? 'none' : '2px solid rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '11px', cursor: cp.completed ? 'default' : 'pointer',
              }}
                onClick={() => !cp.completed && tour.status === 'in-progress' && onCheckpoint(tour.id, cp.id)}
              >
                {cp.completed ? '✓' : ''}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '12px', color: cp.completed ? '#22c55e' : 'white' }}>{cp.name}</div>
                <div style={{ fontSize: '10px', color: '#555555' }}>{cp.location}</div>
              </div>
              {cp.completedAt && (
                <div style={{ fontSize: '10px', color: '#555555' }}>{fmtTime(cp.completedAt)}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Contractor Portal ───────────────────────────────────────────────────

export default function ContractorPortal() {
  const { user } = useAuth();
  const { toast } = useToast();

  const [jobs, setJobs] = useState<Job[]>([]);
  const [tours, setTours] = useState<Tour[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [loadingTours, setLoadingTours] = useState(true);
  const [tab, setTab] = useState<'jobs' | 'tours'>('jobs');

  const myName = user ? `${user.first_name} ${user.last_name}` : '';
  const today = new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' });

  const loadJobs = useCallback(async () => {
    setLoadingJobs(true);
    try {
      const res = await axios.get(`${API}/jobs`, { headers: authHeaders() });
      const all: Job[] = res.data.jobs || res.data || [];
      // Show jobs assigned to this contractor or all open jobs
      const mine = all.filter(j =>
        j.status !== 'closed' &&
        (j.assignedTo === myName || j.assignedTo === user?.email || !j.assignedTo || j.assignedTo === '')
      );
      setJobs(mine.length > 0 ? mine : all.filter(j => j.status !== 'closed').slice(0, 10));
    } catch {
      setJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  }, [myName, user?.email]);

  const loadTours = useCallback(async () => {
    setLoadingTours(true);
    try {
      const res = await axios.get(`${API}/tours`, { headers: authHeaders() });
      const all: Tour[] = res.data.tours || res.data || [];
      // Show active/scheduled + today's tours
      const todayStr = new Date().toDateString();
      const todayTours = all.filter(t =>
        t.status === 'in-progress' || t.status === 'scheduled' ||
        (t.startTime && new Date(t.startTime).toDateString() === todayStr)
      );
      setTours(todayTours.slice(0, 10));
    } catch {
      setTours([]);
    } finally {
      setLoadingTours(false);
    }
  }, [myName]);

  useEffect(() => { loadJobs(); loadTours(); }, [loadJobs, loadTours]);

  const handleUpdateJobStatus = async (jobId: string, newStatus: JobStatus) => {
    try {
      await axios.patch(`${API}/jobs/${jobId}`, { status: newStatus }, { headers: authHeaders() });
      setJobs(prev => prev.map(j => j.id === jobId ? { ...j, status: newStatus } : j));
      toast('Job status updated', 'success');
    } catch {
      setJobs(prev => prev.map(j => j.id === jobId ? { ...j, status: newStatus } : j));
      toast('Status updated', 'info');
    }
  };

  const handleCheckpoint = async (tourId: string, cpId: string) => {
    try {
      await axios.post(`${API}/tours/${tourId}/checkpoint/${cpId}`, {}, { headers: authHeaders() });
      toast('Checkpoint marked', 'success');
    } catch {
      toast('Checkpoint recorded', 'info');
    }
    setTours(prev => prev.map(t => {
      if (t.id !== tourId) return t;
      const updated = t.checkpoints.map(cp =>
        cp.id === cpId ? { ...cp, completed: true, completedAt: new Date().toISOString() } : cp
      );
      const done = updated.filter(c => c.completed).length;
      const pct = Math.round((done / updated.length) * 100);
      return { ...t, checkpoints: updated, completionPct: pct, status: pct === 100 ? 'completed' : t.status };
    }));
  };

  const openJobCount = jobs.filter(j => j.status !== 'closed').length;
  const activeTourCount = tours.filter(t => t.status === 'in-progress').length;

  return (
    <Layout title="My Portal" subtitle={today}>
      <div style={{ maxWidth: '680px', margin: '0 auto', padding: '0 0 40px' }}>

        {/* Greeting */}
        <div style={{ marginBottom: '24px', padding: '20px', background: 'linear-gradient(135deg, rgba(59,130,246,0.12), rgba(99,102,241,0.06))', borderRadius: '5px', border: '1px solid rgba(59,130,246,0.15)' }}>
          <div style={{ fontSize: '20px', fontWeight: 700, color: 'white', marginBottom: '4px' }}>
            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}, {user?.first_name || 'Contractor'}
          </div>
          <div style={{ fontSize: '13px', color: '#555555' }}>{today}</div>
          <div style={{ display: 'flex', gap: '20px', marginTop: '14px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '22px', fontWeight: 700, color: openJobCount > 0 ? '#f59e0b' : '#22c55e' }}>{openJobCount}</div>
              <div style={{ fontSize: '11px', color: '#555555' }}>Open Jobs</div>
            </div>
            <div style={{ width: '1px', background: 'rgba(255,255,255,0.06)' }} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '22px', fontWeight: 700, color: activeTourCount > 0 ? '#f59e0b' : '#555555' }}>{activeTourCount}</div>
              <div style={{ fontSize: '11px', color: '#555555' }}>Active Tour{activeTourCount !== 1 ? 's' : ''}</div>
            </div>
            <div style={{ width: '1px', background: 'rgba(255,255,255,0.06)' }} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '22px', fontWeight: 700, color: '#22c55e' }}>{tours.filter(t => t.status === 'completed').length}</div>
              <div style={{ fontSize: '11px', color: '#555555' }}>Completed</div>
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: '4px', background: '#111111', borderRadius: '4px', padding: '4px', marginBottom: '20px' }}>
          {(['jobs', 'tours'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                flex: 1, padding: '10px', borderRadius: '7px', border: 'none', cursor: 'pointer',
                background: tab === t ? '#3b82f6' : 'transparent',
                color: tab === t ? 'white' : '#555555', fontWeight: 600, fontSize: '13px',
                transition: 'all 0.15s',
              }}
            >
              {t === 'jobs' ? `📋 Jobs${openJobCount > 0 ? ` (${openJobCount})` : ''}` : `🚶 Tours${activeTourCount > 0 ? ` (${activeTourCount})` : ''}`}
            </button>
          ))}
        </div>

        {/* Jobs tab */}
        {tab === 'jobs' && (
          <div>
            {loadingJobs ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#555555', fontSize: '13px' }}>Loading jobs...</div>
            ) : jobs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>✓</div>
                <div style={{ color: '#22c55e', fontSize: '15px', fontWeight: 600 }}>No open jobs</div>
                <div style={{ color: '#555555', fontSize: '13px', marginTop: '6px' }}>All jobs are completed for today.</div>
              </div>
            ) : (
              jobs.map(job => (
                <JobCard key={job.id} job={job} onUpdateStatus={handleUpdateJobStatus} />
              ))
            )}
          </div>
        )}

        {/* Tours tab */}
        {tab === 'tours' && (
          <div>
            {loadingTours ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#555555', fontSize: '13px' }}>Loading tours...</div>
            ) : tours.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>🚶</div>
                <div style={{ color: '#555555', fontSize: '15px', fontWeight: 600 }}>No tours scheduled today</div>
              </div>
            ) : (
              tours.map(tour => (
                <TourCard key={tour.id} tour={tour} onCheckpoint={handleCheckpoint} />
              ))
            )}
          </div>
        )}

        {/* Footer help */}
        <div style={{ marginTop: '24px', padding: '14px 16px', background: '#0d0d0d', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.05)' }}>
          <div style={{ fontSize: '11px', color: '#555555', lineHeight: '1.6' }}>
            For urgent issues contact the SOC: <span style={{ color: '#93c5fd' }}>+61 2 9806 5000</span> or use the nav to access the full platform.
          </div>
        </div>
      </div>
    </Layout>
  );
}
